import { gql } from '@apollo/client';

/**
 * ATS-side hydration query for recommendation rows (batch or search). Backend:
 * candidatesByIds(ids: [String!]!): [Candidate!]! — packages/api/src/candidates
 * (candidates.resolver.ts / candidates.service.ts), JWT-guarded like every
 * other candidate query. Drops unknown ids, re-sorts to the caller's id order.
 *
 * Field aliases below exist because CandidateResume / CandidatePhone don't
 * literally carry `title` / `fileName` / `remoteFilePath` / `phone` columns —
 * they alias onto the closest real entity fields so the response matches the
 * AtsCandidate / AtsResume shapes in ./types.ts exactly:
 *   - phones.phone            -> CandidatePhone.phoneNumber
 *   - resume.title/.fileName  -> CandidateResume.resumeFileName (no separate
 *                                 "title" concept exists on the entity)
 *   - resume.remoteFilePath   -> CandidateResume.originalUrl (the value the
 *                                 existing GET_SIGNED_URL mutation expects as
 *                                 its `remoteFilePath` arg — see
 *                                 CandidateWorkspacePanel/index.tsx:127-131)
 */
// Mirrors the candidate shape /new-applicants feeds CandidateWorkspacePanel
// (Pages/job-order/gql GET_NEW_APPLICANTS_GROUPED) so the recsys detail modal
// can reuse that panel verbatim — same width/résumé view + Notes + Apply tabs.
export const CANDIDATES_BY_IDS = gql`
  query CandidatesByIds($ids: [String!]!) {
    candidatesByIds(ids: $ids) {
      id
      fullName
      jobTitle
      companyName
      linkedInUrl
      phones {
        phoneNumber
        isPrimary
      }
      emails {
        email
        isPrimary
      }
      resume {
        id
        resumeFileName
        originalUrl
        pdfVersionUrl
        createdAt
      }
      recruiterOwner {
        id
        fullName
      }
      candidateStats {
        ownershipExpiration
        recruitingOwner {
          id
          fullName
        }
      }
    }
  }
`;

/**
 * Powers the applied-candidate exclusion filter: candidates who already have a
 * JobApplicant row for the currently-selected job order are dropped from
 * match/search results. 2b will union shortlist/declined dispositions into
 * this same list server-side — no frontend change needed then.
 */
export const APPLIED_CANDIDATE_IDS_FOR_JOB = gql`
  query AppliedCandidateIdsForJob($jobOrderId: String!, $candidateIds: [String!]!) {
    appliedCandidateIdsForJob(jobOrderId: $jobOrderId, candidateIds: $candidateIds)
  }
`;

/**
 * Seeds for Collaborated Search (kan-82): distinct candidate ids with ANY
 * Interview row for the job order (all statuses), most-recent-interview-first,
 * null-candidate rows skipped server-side. Backend: job-order.resolver.ts,
 * JWT-guarded like appliedCandidateIdsForJob. Uncapped server-side — the page
 * slices to the 15 most recent before calling recsys.
 */
export const INTERVIEWED_CANDIDATE_IDS_FOR_JOB = gql`
  query InterviewedCandidateIdsForJob($jobOrderId: String!) {
    interviewedCandidateIdsForJob(jobOrderId: $jobOrderId)
  }
`;

/**
 * Resolves the free text in the filters rail's location box to the radius
 * filter's center (kan-83). Backend: job-order/location-resolve.resolver.ts,
 * JWT-guarded like interviewedCandidateIdsForJob above; a thin passthrough to
 * UtilsService.locationFinder (Redis-cached, Google geocode on miss), so the
 * page adds no cache of its own. Coordinates arrive as Floats — never parse
 * them client-side.
 *
 * null = no usable match. The backend cannot tell that apart from a Google-side
 * failure, so the page treats null as "couldn't find it" and only falls back to
 * the job's stored coordinates when the CALL ITSELF fails.
 */
export const RESOLVE_LOCATION = gql`
  query ResolveLocation($locationText: String!) {
    resolveLocation(locationText: $locationText) {
      latitude
      longitude
      formattedAddress
    }
  }
`;

/**
 * Of the given candidateIds, returns the subset the current recruiter is
 * authorized to see. recsys is PII-blind and returns private candidates' ids in
 * match/search results just like any other; this ATS-side gate (same rule as
 * candidatesByIds, but without hydration) lets the page drop private candidates
 * the recruiter can't access BEFORE they render as blank rows.
 */
export const ACCESSIBLE_CANDIDATE_IDS = gql`
  query AccessibleCandidateIds($candidateIds: [String!]!) {
    accessibleCandidateIds(candidateIds: $candidateIds)
  }
`;

/**
 * Applicant-mode row hydration (kan-119): for the visible candidate ids, their
 * JobApplicant row on the selected job order — one per candidate, the newest
 * application if several. Backend: job-order.resolver.ts, JWT-guarded with the
 * job-order access check, ids run through the same private-candidate gate as
 * the disposition reads; capped at 400 ids (the page asks for one page of 8).
 * JobAppStatus comes back as the GraphQL enum KEY ('APPLIED', 'CALLED', …).
 * The row id is what CandidateWorkspacePanel's Set Status / Decline mutate.
 */
export const JOB_APPLICANTS_FOR_CANDIDATES = gql`
  query JobApplicantsForCandidates($jobOrderId: String!, $candidateIds: [String!]!) {
    jobApplicantsForCandidates(jobOrderId: $jobOrderId, candidateIds: $candidateIds) {
      id
      candidateId
      JobAppStatus
      createdAt
      aiScore
      aiScoreReason
      aiScoredResumeId
      aiScoredAt
    }
  }
`;

/**
 * Story 2b dispositions (Shortlist / Decline persistence, RecsysDispositions
 * table). recsysDispositionsForJob returns BOTH statuses for the job in one
 * call — the frontend (context.tsx) splits by status client-side to drive
 * both the Shortlist(n)/Declined(n) tab counts and their row lists; the
 * returned candidateIds are then hydrated via CANDIDATES_BY_IDS above.
 */
export const RECSYS_DISPOSITIONS_FOR_JOB = gql`
  query RecsysDispositionsForJob($jobOrderId: String!) {
    recsysDispositionsForJob(jobOrderId: $jobOrderId) {
      id
      candidateId
      status
      createdAt
    }
  }
`;

/**
 * Upsert by unique (jobOrderId, candidateId): inserts a new disposition row
 * or flips status on the existing one (Shortlist<->Decline toggle). Powers
 * both the detail-modal actions and the Batch/Search row actions.
 */
export const SET_RECSYS_DISPOSITION = gql`
  mutation SetRecsysDisposition(
    $jobOrderId: String!
    $candidateId: String!
    $status: RecsysDispositionStatusEnum!
  ) {
    setRecsysDisposition(jobOrderId: $jobOrderId, candidateId: $candidateId, status: $status) {
      id
      candidateId
      status
    }
  }
`;

/**
 * Deletes the disposition row for (jobOrderId, candidateId) — powers Restore
 * on the Declined tab. Idempotent server-side.
 */
export const REMOVE_RECSYS_DISPOSITION = gql`
  mutation RemoveRecsysDisposition($jobOrderId: String!, $candidateId: String!) {
    removeRecsysDisposition(jobOrderId: $jobOrderId, candidateId: $candidateId) {
      message
      status
    }
  }
`;

/**
 * Bulk-apply for the Shortlist tab: applies each candidate to the selected
 * job order via the existing createSubmissionOnApply path, then clears their
 * disposition row (they're excluded going forward via the JobApplicant
 * branch of the union).
 */
export const BULK_APPLY_SHORTLISTED = gql`
  mutation BulkApplyShortlisted($jobOrderId: String!, $candidateIds: [String!]!) {
    bulkApplyShortlisted(jobOrderId: $jobOrderId, candidateIds: $candidateIds) {
      message
      status
    }
  }
`;

/**
 * Per-job-order search settings (kan-80): the page's complete search
 * configuration, saved against the JOB ORDER and shared by every user who can
 * access it. Read when the active job order changes — always network-only, the
 * row is mutated by other users, so a cached read would serve a superseded
 * configuration. `settings` is an opaque JSONObject scalar sanitized in
 * utils.ts before it touches page state.
 */
export const JOB_ORDER_REC_SEARCH_SETTINGS = gql`
  query JobOrderRecSearchSettings($jobOrderId: String!) {
    jobOrderRecSearchSettings(jobOrderId: $jobOrderId) {
      id
      jobOrderId
      settings
      updatedAt
    }
  }
`;

/**
 * Overwrites the job order's saved search configuration — fired (and forgotten)
 * on every executed search in either mode; last write wins.
 */
export const UPSERT_JOB_ORDER_REC_SEARCH_SETTINGS = gql`
  mutation UpsertJobOrderRecSearchSettings($jobOrderId: String!, $settings: JSONObject!) {
    upsertJobOrderRecSearchSettings(jobOrderId: $jobOrderId, settings: $settings) {
      id
      updatedAt
    }
  }
`;

/**
 * Company selector for the "Client company" dropdown. Re-declared here (not
 * imported from Pages/job-order/gql) per the lane contract — leaner than
 * FETCH_All_JOB_ORDER_COMPANIES since this page only needs id/name.
 */
// Scoped to the current user: returns only companies they OWN, have shared
// access to, or where they are an assigned recruiter on an ACTIVE job order
// (Admin/Executive still see all; non-privileged users do NOT see unowned
// companies — includeUnowned:false), AND only companies that have at least one
// ACTIVE job order (onlyWithActiveJobOrders:true). Deliberately NOT
// companiesForDropDown, which is unscoped (all companies).
export const REC_COMPANIES_DROPDOWN = gql`
  query RecCompaniesOfCurrentUser($page: Int!, $limit: Int!, $searchText: String) {
    fetchCompaniesOfCurrentUser(
      payload: { page: $page, limit: $limit }
      filters: { searchText: $searchText }
      includeUnowned: false
      onlyWithActiveJobOrders: true
    ) {
      id
      name
    }
  }
`;

/**
 * Active job orders for the selected company, feeding the "Active job order"
 * dropdown and the "Matching for" summary row. Re-declared here (not imported
 * from Pages/job-order/gql) per the lane contract. companyLocation's city/state
 * seed the filters-rail location box, and its latitude/longitude are the
 * instant center shown before RESOLVE_LOCATION answers — and the silent
 * fallback when that call fails (kan-83).
 * accessScoped:true makes the backend honour statusSearch here and, for a
 * company the recruiter only reaches through an assigned job order, return only
 * those assigned job orders (kan-78 item 3).
 */
export const REC_ACTIVE_JOB_ORDERS = gql`
  query RecJobOrdersForCompany(
    $page: Int!
    $limit: Int!
    $companyId: String!
    $statusSearch: String
  ) {
    fetchAllJobOrders(
      payload: { page: $page, limit: $limit }
      filters: { companyId: $companyId, statusSearch: $statusSearch, accessScoped: true }
    ) {
      jobOrders {
        id
        jobTitle
        advertisedJobDescription
        status
        createdAt
        newApplications
        totalApplications
        category {
          id
          name
        }
        companyLocation {
          city
          state
          country
          latitude
          longitude
        }
        company {
          id
          name
        }
      }
      count
    }
  }
`;
